import React, { useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { Breakpoint, Dialog, SxProps, useTheme } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { borderRadiusValues } from 'shared/lib/utils/uiConstants';
import IconCross from 'shared/icons/cross';
import { makeTransition } from 'shared/lib/utils/utils';

export type ModalProps = {
  isOpen: boolean;
  isMobileOffset?: boolean;
  handleClose: () => void;
  children?: React.ReactNode;
  iconClose?: React.ReactElement;
  disableCloseIcon?: boolean;
  sx?: SxProps;
  maxWidth?: false | Breakpoint | undefined;
  iconStyle?: React.CSSProperties;
};

export default observer(function Modal({
  isOpen,
  children,
  handleClose,
  iconClose,
  disableCloseIcon,
  iconStyle,
  isMobileOffset,
  sx,
  ...otherProps
}: ModalProps) {
  const {
    spacings: { sp },
  } = useContext(UiContext);

  const theme = useTheme();

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="noscroll"
      fullWidth={true}
      maxWidth="tablet"
      {...otherProps}
      sx={{
        ...sx,
        '& .MuiDialog-paper': {
          'display': 'flex',
          'flexDirection': 'column',
          'gap': sp.s50,
          'p': sp.s150,
          'm': 0,
          'borderRadius': borderRadiusValues.medium,
          'width': `calc(100% - ${theme.spacing(sp.s160)})`,
          // @ts-ignore
          ...(sx?.['& .MuiDialog-paper'] || {}),
          ...(isMobileOffset
            ? {
                position: 'absolute',
                top: `${theme.spacing(sp.s80)}`,
              }
            : {}),
          'transition': makeTransition(['top'], { duration: '500ms' }),
          'msOverflowStyle': 'none' /* IE and Edge */,
          'scrollbarWidth': 'none',
          '.noscroll::-webkit-scrollbar': { display: 'none' },
        },
      }}
    >
      {disableCloseIcon
        ? ''
        : iconClose || (
            <IconCross
              sizeKey="24x24"
              style={{
                position: 'absolute',
                top: theme.spacing(sp.s80),
                right: theme.spacing(sp.s80),
                ...iconStyle,
              }}
              onClick={handleClose}
            ></IconCross>
          )}
      {children}
    </Dialog>
  );
});
